/**
 * 🔐 Session Cache — Tek Kaynak (Single Source of Truth)
 *
 * Tüm bileşenlerin aynı session'ı tekrar tekrar Supabase'den sorgulamasını önler.
 * Home, Login, useAuthGuard, NotificationProvider hepsi bu cache'i kullanır.
 *
 * Mekanizma:
 * - İlk getSession() çağrısını cache'ler
 * - 30sn TTL ile stale olana kadar cache'den döner
 * - onAuthStateChange ile cache'i otomatik invalidate eder
 * - Concurrent çağrılar tek bir ağ isteğine birleştirilir (request dedup)
 */
import { supabase } from '@/lib/supabase'
import type { Session, User } from '@supabase/supabase-js'

const CACHE_TTL_MS = 30_000 // 30 saniye

interface CachedSession {
  session: Session | null
  user: User | null
  timestamp: number
}

let _cache: CachedSession | null = null
let _inflight: Promise<CachedSession> | null = null

/**
 * Cache'i invalidate et — onAuthStateChange veya logout durumlarında çağır
 */
export function invalidateSessionCache(): void {
  _cache = null
  _inflight = null
}

/**
 * Cache'lenmiş session'ı döndür.
 * Cache miss veya stale ise Supabase'den çeker ve cache'ler.
 * Concurrent çağrılar tek bir ağ isteğine birleştirilir.
 */
export async function getCachedSession(): Promise<{ session: Session | null; user: User | null }> {
  // Cache hit — fresh
  if (_cache && Date.now() - _cache.timestamp < CACHE_TTL_MS) {
    return { session: _cache.session, user: _cache.user }
  }

  // Request deduplication — aynı anda birden fazla çağrı yapılırsa tek istek
  if (_inflight) {
    const result = await _inflight
    return { session: result.session, user: result.user }
  }

  _inflight = _fetchAndCache()

  try {
    const result = await _inflight
    return { session: result.session, user: result.user }
  } finally {
    _inflight = null
  }
}

/**
 * Cache'den senkron okuma — sadece cache varsa döner, yoksa null.
 * UI'da anında kontrol için (ağ isteği YAPMAZ).
 */
export function peekCachedSession(): { session: Session | null; user: User | null } | null {
  if (_cache && Date.now() - _cache.timestamp < CACHE_TTL_MS) {
    return { session: _cache.session, user: _cache.user }
  }
  return null
}

async function _fetchAndCache(): Promise<CachedSession> {
  const { data: { session } } = await supabase.auth.getSession()
  const user = session?.user ?? null

  const cached: CachedSession = {
    session,
    user,
    timestamp: Date.now(),
  }

  _cache = cached
  return cached
}

// ─── Auto-invalidation: Auth state değiştiğinde cache'i temizle ───
supabase.auth.onAuthStateChange((event) => {
  if (event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED' || event === 'SIGNED_IN') {
    invalidateSessionCache()
  }
})
